import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TermLessonCalendar } from '@/components/schedule/TermLessonCalendar';
import { BookingWeeklySchedule } from '@/components/schedule/BookingWeeklySchedule';
import { RemoveStudentEnrollmentDialog } from '@/components/lessons/RemoveStudentEnrollmentDialog';
import { useStudentProfileSchedule } from '@/hooks/useStudentProfileSchedule';
import { bookingService } from '@/services/booking';
import type { ScheduleOccurrence } from '@/utils/scheduleOccurrenceTypes';

type CalendarView = 'month' | 'week';

export interface StudentLessonCalendarModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  academyId: string;
  studentId: string;
  studentDisplayName: string;
  makeupLessonEnabled?: boolean;
  /** Called after a successful removal so parent lists can refresh */
  onEnrollmentChanged?: () => void;
}

export function StudentLessonCalendarModal({
  open,
  onOpenChange,
  academyId,
  studentId,
  studentDisplayName,
  makeupLessonEnabled = false,
  onEnrollmentChanged,
}: StudentLessonCalendarModalProps) {
  const [view, setView] = useState<CalendarView>('month');
  const [removeTarget, setRemoveTarget] = useState<ScheduleOccurrence | null>(null);
  const [removing, setRemoving] = useState(false);
  const { occurrences, loading, error, refetch } = useStudentProfileSchedule(
    academyId,
    studentId,
    open
  );

  const handleRemoveFromLesson = useCallback((occurrence: ScheduleOccurrence) => {
    setRemoveTarget(occurrence);
  }, []);

  const handleRemoveConfirm = useCallback(
    async (params: { scope: 'session' | 'term'; grantMakeupCredit: boolean }) => {
      if (!removeTarget) return;
      const sessionDateYmd = format(removeTarget.date, 'yyyy-MM-dd');
      setRemoving(true);
      try {
        const res = await bookingService.removeEnrollment(removeTarget.bookingId, {
          academyId,
          scope: params.scope,
          sessionDate: params.scope === 'session' ? sessionDateYmd : undefined,
          grantMakeupCredit: params.grantMakeupCredit,
        });
        if (!res.success) {
          throw new Error((res as { error?: string }).error || 'Failed to remove student');
        }
        toast.success(
          params.scope === 'term'
            ? 'Student removed from term lesson'
            : res.data?.makeupCreditGranted
              ? 'Student removed and make-up credit granted'
              : 'Student removed from this session'
        );
        if (
          params.scope === 'session' &&
          params.grantMakeupCredit &&
          !res.data?.makeupCreditGranted &&
          res.data?.makeupCreditSkippedReason
        ) {
          toast.error(res.data.makeupCreditSkippedReason);
        }
        setRemoveTarget(null);
        await refetch();
        onEnrollmentChanged?.();
      } finally {
        setRemoving(false);
      }
    },
    [academyId, onEnrollmentChanged, refetch, removeTarget]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal={!removeTarget}>
        <DialogContent className="flex max-h-[min(90vh,800px)] w-[calc(100vw-1rem)] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:w-full">
          <DialogHeader className="shrink-0 space-y-1 border-b border-border px-6 py-4 text-left">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <Calendar className="h-5 w-5 shrink-0 text-primary" aria-hidden />
              Lesson calendar — {studentDisplayName}
            </DialogTitle>
            <DialogDescription>
              Scheduled sessions for current enrollments (pending and confirmed).
            </DialogDescription>
            <div className="flex gap-1 pt-2">
              <Button
                type="button"
                size="sm"
                variant={view === 'month' ? 'default' : 'outline'}
                onClick={() => setView('month')}
              >
                Month
              </Button>
              <Button
                type="button"
                size="sm"
                variant={view === 'week' ? 'default' : 'outline'}
                onClick={() => setView('week')}
              >
                Week
              </Button>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-6 w-6 animate-spin" aria-hidden />
                Loading calendar…
              </div>
            ) : error ? (
              <div className="space-y-3 py-12 text-center">
                <p className="text-sm text-destructive">{error}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                  Try again
                </Button>
              </div>
            ) : occurrences.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No upcoming sessions for current enrollments.
              </p>
            ) : view === 'month' ? (
              <TermLessonCalendar
                occurrences={occurrences}
                variant="chips"
                onRemoveFromLesson={handleRemoveFromLesson}
              />
            ) : (
              <BookingWeeklySchedule
                occurrences={occurrences}
                onOccurrenceClick={handleRemoveFromLesson}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RemoveStudentEnrollmentDialog
        key={
          removeTarget
            ? `remove-${removeTarget.bookingId}-${format(removeTarget.date, 'yyyy-MM-dd')}`
            : 'remove-closed'
        }
        isOpen={!!removeTarget}
        studentName={removeTarget?.studentName ?? studentDisplayName}
        lessonName={removeTarget?.lessonName ?? ''}
        sessionDateLabel={
          removeTarget ? format(removeTarget.date, 'EEEE, MMM d, yyyy') : ''
        }
        sessionDateYmd={removeTarget ? format(removeTarget.date, 'yyyy-MM-dd') : ''}
        bookingId={removeTarget?.bookingId ?? ''}
        makeupLessonEnabled={makeupLessonEnabled}
        isTermEnrollment={
          removeTarget?.bookingScope === 'term' || removeTarget?.bookingScope === 'package'
        }
        isSubmitting={removing}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirm}
      />
    </>
  );
}
